import api from './api';

export interface RecetaComponenteDto {
  codigoArticulo: string;
  descripcion?: string | null;
  cantidad: number;
}

export interface RecetaProductoDto {
  codigoArticulo: string;
  descripcion?: string | null;
  cantidad: number;
  original?: string | null;
  descripcionOriginal?: string | null;
  cantidadOriginal?: number | null;
}

export interface RecetaDto {
  codigoReceta: string;
  descripcion: string;
  productoTerminado?: RecetaProductoDto | null;
  componentes: RecetaComponenteDto[];
}

export interface RecetaListItemDto {
  codigoReceta: string;
  descripcion: string;
  codigoProductoTerminado?: string | null;
  descripcionProductoTerminado?: string | null;
  cantidadComponentes: number;
  fechaUltModificacion?: string | null;
  costoUnitario?: number | null;
  unidadMedidaBase?: string | null;
}

export interface RecetaPagedResult {
  items: RecetaListItemDto[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
}

const normalizePaged = (data: Record<string, unknown>): RecetaPagedResult => ({
  items: ((data.items ?? data.Items ?? []) as RecetaListItemDto[]),
  totalCount: Number(data.totalCount ?? data.TotalCount ?? 0),
  pageNumber: Number(data.pageNumber ?? data.PageNumber ?? 1),
  pageSize: Number(data.pageSize ?? data.PageSize ?? 10),
  totalPages: Number(data.totalPages ?? data.TotalPages ?? 0),
});

const recetaService = {
  getRecetas: async (params: {
    pageNumber?: number;
    pageSize?: number;
    searchTerm?: string;
  }): Promise<RecetaPagedResult> => {
    const resp = await api.get('/api/Recetas', {
      params: {
        PageNumber: params.pageNumber ?? 1,
        PageSize: params.pageSize ?? 10,
        SearchTerm: params.searchTerm || undefined,
      },
    });
    return normalizePaged(resp.data as Record<string, unknown>);
  },

  getReceta: async (codigoReceta: string): Promise<RecetaDto> => {
    const resp = await api.get(`/api/Recetas/${encodeURIComponent(codigoReceta)}`);
    return resp.data as RecetaDto;
  },

  getPorProducto: async (codigoArticulo: string): Promise<RecetaDto | null> => {
    try {
      const resp = await api.get(`/api/Recetas/por-producto/${encodeURIComponent(codigoArticulo)}`);
      return resp.data as RecetaDto;
    } catch (error: unknown) {
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status === 404) return null;
      throw error;
    }
  },

  create: async (dto: {
    codigoReceta: string;
    descripcion: string;
    productoTerminado: RecetaProductoDto;
    componentes: RecetaComponenteDto[];
  }): Promise<RecetaDto> => {
    const resp = await api.post('/api/Recetas', dto);
    return resp.data as RecetaDto;
  },

  update: async (
    codigoReceta: string,
    dto: {
      descripcion: string;
      productoTerminado: RecetaProductoDto;
      componentes: RecetaComponenteDto[];
    },
  ): Promise<RecetaDto> => {
    const resp = await api.put(`/api/Recetas/${encodeURIComponent(codigoReceta)}`, dto);
    return resp.data as RecetaDto;
  },

  remove: async (codigoReceta: string): Promise<void> => {
    await api.delete(`/api/Recetas/${encodeURIComponent(codigoReceta)}`);
  },

  actualizarCostoProductoTerminado: async (codigoArticulo: string, costo: number): Promise<void> => {
    await api.put('/api/Recetas/actualizar-costo-producto-terminado', {
      codigoArticulo,
      costo,
    });
  },
};

export default recetaService;
